<script lang="ts" setup>
    import type {
        ChatMessageActions,
        MessageFeedback,
        RevisedAnswer,
        UIChatMessage,
    } from 'models'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'
    import PkChatbotError from './PkChatbotError.vue'
    import PkChatbotFeedbackForm from './PkChatbotFeedbackForm.vue'
    import PkRelativeTime from '../PkRelativeTime.vue'
    import { useI18n } from 'vue-i18n'
    import {
        useTemplateRef,
        ref,
        watch,
        onMounted,
        computed,
        nextTick,
    } from 'vue'
    import {
        getContrast,
        getPartState,
        isTextPart,
        isToolPart,
        isStreamingPart,
        getPartIcon,
        getToolPartLabel,
    } from './utils'
    import { toKebabCase } from 'utils'

    const props = defineProps<{
        status?: 'submitted' | 'streaming' | 'ready' | 'error'
        messages?: UIChatMessage[]
        name?: string
        error?: Error
        actions?: ChatMessageActions[]
        mainColor?: string
        revisedAnswers?: RevisedAnswer[]
        messageFeedbacks?: MessageFeedback[]
        disableHeightAdjustment?: boolean
        showMessageDateTime?: boolean
        showMessageTokensCount?: boolean
        showAllMessageParts?: boolean
        // TODO: move feedback in a separate component to avoid passing these props
        feedbackMessageId?: string
        feedbackLoading?: boolean
        feedbackSubmitted?: boolean
        feedbackError?: string
    }>()

    const emit = defineEmits<{
        (e: 'show-info', message: UIChatMessage): void
        (e: 'regenerate'): void
        (e: 'revise', message: UIChatMessage): void
        (e: 'upvote', message: UIChatMessage): void
        (e: 'downvote', message: UIChatMessage): void
        (e: 'feedback', message: UIChatMessage): void
        (e: 'feedback-submit', comment: string): void
        (e: 'feedback-close'): void
        (e: 'scroll-up'): void
        (e: 'scroll-down'): void
        (e: 'auto-retry'): void
        (e: 'reset-chat'): void
    }>()

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const scrollEl = useTemplateRef<HTMLDivElement>('scrollEl')
    const scrollTimeout = ref<NodeJS.Timeout>()
    const lastScrollTop = ref<number>(0)
    const stopAutoScroll = ref<boolean>(false)
    const contrastColor = computed(() => getContrast(props.mainColor))

    const handleScroll = () => {
        if (!scrollEl.value) {
            return
        }
        const currentScrollTop = scrollEl.value.scrollTop
        if (currentScrollTop < lastScrollTop.value) {
            const delta = lastScrollTop.value - currentScrollTop
            if (delta < 50) {
                stopAutoScroll.value = true
            }
            emit('scroll-up')
        }
        if (currentScrollTop > lastScrollTop.value) {
            emit('scroll-down')
            if (
                currentScrollTop ===
                scrollEl.value.scrollHeight - scrollEl.value.clientHeight
            ) {
                stopAutoScroll.value = false
            }
        }
        lastScrollTop.value = currentScrollTop
    }

    const smootScrollToBottom = () => {
        if (!scrollEl.value) {
            return
        }
        if (scrollTimeout.value) {
            clearTimeout(scrollTimeout.value)
        }
        if (stopAutoScroll.value) {
            return
        }
        scrollTimeout.value = setTimeout(() => {
            scrollEl.value?.scrollTo({
                top: scrollEl.value.scrollHeight,
                behavior: 'smooth',
            })
        }, 10)
    }

    watch(
        () => props.status,
        (newStatus) => {
            if (newStatus === 'ready') {
                smootScrollToBottom()
            }
        },
    )

    const messagesElRefs = useTemplateRef<HTMLDivElement[]>('messagesEl')
    const height = ref<number>(0)
    watch(
        () => props.messages,
        async (messages) => {
            if (messages?.[messages.length - 1]?.role !== 'assistant') {
                stopAutoScroll.value = false
                return
            }

            // Wait for DOM to update before calculating heights
            await nextTick()
            smootScrollToBottom()

            if (props.disableHeightAdjustment) {
                return
            }

            const scrollElHeight = (scrollEl.value?.clientHeight ?? 0) - 48
            // last user message index
            const lastUserMessageIndex = messages
                .map((message) => message.role)
                .lastIndexOf('user')
            const lastUserMessageHeight =
                messagesElRefs.value?.[lastUserMessageIndex]?.clientHeight ?? 0
            const newHeight = scrollElHeight - lastUserMessageHeight
            if (newHeight) {
                height.value = newHeight
            }
        },
        {
            deep: true,
        },
    )

    onMounted(() => {
        smootScrollToBottom()
    })

    const isRevised = (messageId: string) => {
        return props.revisedAnswers?.some((r) => r.messageId === messageId)
    }

    const getMessageFeedback = (messageId: string) =>
        props.messageFeedbacks?.find((f) => f.messageId === messageId)

    const activeMessage = computed(() => {
        return props.messages?.[props.messages.length - 1]
    })
    const activeMessageLastPart = computed(() => {
        const active = activeMessage.value
        if (!active) {
            return null
        }
        return active.parts[active.parts.length - 1]
    })
    const isLoading = computed(() => {
        return props.status === 'submitted' || props.status === 'streaming'
    })
    const isError = computed(() => props.status === 'error')

    const activeMessageLastPartLabel = computed(() => {
        const part = activeMessageLastPart.value
        return getToolPartLabel($t, part)
    })
    const activeMessageLastPartIcon = computed(() => {
        const part = activeMessageLastPart.value
        return getPartIcon(part)
    })

    const getMessageIndexById = (messageId: string) => {
        return props.messages?.findIndex((m) => m.id === messageId)
    }
    const isLastUserMessageIndex = (index?: number) => {
        if (index === undefined || !props.messages) {
            return false
        }
        return index === (props.messages?.length ?? 0) - 1
    }
    const isMessageRegenerateButtonVisible = (index: number) => {
        return (
            isLastUserMessageIndex(index) &&
            props.status === 'ready' &&
            props.actions?.includes('regenerate')
        )
    }
    const isActionButtonVisible = (
        action: ChatMessageActions,
        index: number,
    ) => {
        return (
            ((isLastUserMessageIndex(index) && props.status === 'ready') ||
                !isLastUserMessageIndex(index)) &&
            props.actions?.includes(action)
        )
    }
    const isLastTextPart = (message: UIChatMessage) => {
        const lastPart = message.parts[message.parts.length - 1]
        return isTextPart(lastPart)
    }
    const getPreviousAssistantMessage = (index: number) => {
        return props.messages
            ?.slice(0, index)
            .reverse()
            .find((message) => message.role === 'assistant')
    }
    const getNextAssistantMessage = (index: number) => {
        return props.messages
            ?.slice(index + 1)
            .find((message) => message.role === 'assistant')
    }
    const isDayChanged = (index: number) => {
        const previousAssistantMessage = getPreviousAssistantMessage(index)
        const nextAssistantMessage = getNextAssistantMessage(index)
        if (
            !previousAssistantMessage?.metadata?.createdAt ||
            !nextAssistantMessage?.metadata?.createdAt
        ) {
            return false
        }
        const currentDate = new Date(
            previousAssistantMessage.metadata.createdAt,
        )
        const previousDate = new Date(nextAssistantMessage.metadata.createdAt)
        return (
            currentDate.getFullYear() !== previousDate.getFullYear() ||
            currentDate.getMonth() !== previousDate.getMonth() ||
            currentDate.getDate() !== previousDate.getDate()
        )
    }
    const showMessageFooter = (message: UIChatMessage, index: number) => {
        if (index === 0 || message.role !== 'assistant') {
            return false
        }
        return (
            props.actions?.length ||
            (props.showMessageDateTime &&
                message.metadata?.createdAt !== undefined) ||
            (props.showMessageTokensCount &&
                message.metadata?.totalTokens !== undefined)
        )
    }
    // feedback message auto scroll
    const feedbackMessageEl = ref<(typeof PkChatbotFeedbackForm)[]>()
    watch(
        () => props.feedbackMessageId,
        async () => {
            await nextTick()
            setTimeout(() => {
                if (props.feedbackMessageId) {
                    if (feedbackMessageEl.value?.[0]?.$el) {
                        if (
                            isLastUserMessageIndex(
                                getMessageIndexById(props.feedbackMessageId),
                            )
                        ) {
                            smootScrollToBottom()
                            return
                        }
                        feedbackMessageEl.value[0].$el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        })
                    }
                }
            }, 10)
        },
    )
</script>

<template>
    <div
        ref="scrollEl"
        class="pk-chatbot-messages"
        :style="{
            '--chatbot-main-color': mainColor,
            '--chatbot-contrast-color': contrastColor,
        }"
        @scroll="handleScroll">
        <div class="pk-chatbot-messages__wrapper">
            <template v-for="(message, index) in messages" :key="message.id">
                <div
                    v-if="
                        index > 0 &&
                        message.role === 'user' &&
                        isDayChanged(index)
                    "
                    class="pk-chatbot-divider">
                    <span class="pk-chatbot-divider__label">
                        {{
                            $d(
                                new Date(
                                    getNextAssistantMessage(index)?.metadata
                                        ?.createdAt ?? '',
                                ),
                                'short',
                            )
                        }}
                    </span>
                </div>
                <div
                    ref="messagesEl"
                    class="pk-chatbot-message"
                    :class="[
                        `pk-chatbot-message--${message.role}`,
                        {
                            'pk-chatbot-message--loading':
                                isLastUserMessageIndex(index) &&
                                isLoading &&
                                message.role === 'assistant',
                        },
                    ]"
                    :style="{
                        minHeight:
                            isLastUserMessageIndex(index) &&
                            message.role === 'assistant' &&
                            !isError
                                ? `${height}px`
                                : undefined,
                    }">
                    <template
                        v-for="(part, partIndex) in message.parts"
                        :key="partIndex">
                        <div
                            v-if="part.type === 'text' && part.text.trim()"
                            class="pk-chatbot-message__text">
                            <PkStreamingMarkdown
                                v-if="message.role === 'assistant'"
                                class="wysiwyg"
                                :markdown="part.text"
                                :loading="
                                    index === (messages?.length ?? 0) - 1 &&
                                    status === 'streaming'
                                " />
                            <template v-else>
                                {{ part.text }}
                            </template>
                        </div>
                        <slot
                            v-else-if="
                                isToolPart(part) && !isStreamingPart(part)
                            "
                            :key="getPartState(part)"
                            :name="part.type"
                            v-bind="{
                                message,
                                part,
                                index,
                                isLoading,
                            }">
                            <template v-if="showAllMessageParts">
                                <div class="pk-chatbot-message__loading-info">
                                    <VvIcon
                                        v-if="getPartIcon(part)"
                                        :name="getPartIcon(part)!" />
                                    <code class="font-mono rounded text-10">
                                        {{ toKebabCase(part.type) }}
                                    </code>
                                </div>
                            </template>
                        </slot>
                    </template>
                    <transition mode="out-in">
                        <div
                            v-if="
                                isLoading &&
                                isLastUserMessageIndex(index) &&
                                !isLastTextPart(message) &&
                                message.role === 'assistant'
                            "
                            :key="`loading-info-${message.id}`"
                            class="pk-chatbot-message__loading-info">
                            <VvIcon name="line-md:loading-loop" />
                            <transition mode="out-in">
                                <VvIcon
                                    v-if="activeMessageLastPartIcon"
                                    :key="activeMessageLastPartIcon"
                                    :name="activeMessageLastPartIcon" />
                            </transition>
                            <transition mode="out-in">
                                <span
                                    v-if="activeMessageLastPartLabel"
                                    :key="activeMessageLastPartLabel"
                                    class="text-smaller">
                                    {{ activeMessageLastPartLabel }}
                                </span>
                            </transition>
                        </div>
                    </transition>
                    <transition mode="out-in">
                        <div
                            v-if="showMessageFooter(message, index)"
                            class="pk-chatbot-message__footer">
                            <VvButtonGroup modifiers="compact" class="mr-auto">
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isMessageRegenerateButtonVisible(
                                                index,
                                            )
                                        "
                                        icon="ri:reset-right-line"
                                        modifiers="action-quiet-small"
                                        :title="$t('action.regenerate')"
                                        @click.stop="$emit('regenerate')" />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isActionButtonVisible(
                                                'show-info',
                                                index,
                                            )
                                        "
                                        icon="ri:information-line"
                                        :title="$t('action.getMoreInfo')"
                                        modifiers="action-quiet-small"
                                        @click.stop="
                                            $emit('show-info', message)
                                        " />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isActionButtonVisible(
                                                'revise',
                                                index,
                                            )
                                        "
                                        :icon="
                                            isRevised(message.id)
                                                ? 'ri:file-edit-fill'
                                                : 'ri:file-edit-line'
                                        "
                                        :title="
                                            isRevised(message.id)
                                                ? $t('action.editRevise')
                                                : $t('action.createRevise')
                                        "
                                        modifiers="action-quiet-small"
                                        :class="{
                                            'text-brand': isRevised(message.id),
                                        }"
                                        @click.stop="
                                            $emit('revise', message)
                                        " />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isActionButtonVisible(
                                                'upvote',
                                                index,
                                            )
                                        "
                                        :icon="
                                            getMessageFeedback(message.id)
                                                ?.vote === 'upvote'
                                                ? 'ri:thumb-up-fill'
                                                : 'ri:thumb-up-line'
                                        "
                                        :title="$t('action.upvote')"
                                        modifiers="action-quiet-small"
                                        @click.stop="
                                            $emit('upvote', message)
                                        " />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isActionButtonVisible(
                                                'downvote',
                                                index,
                                            )
                                        "
                                        :icon="
                                            getMessageFeedback(message.id)
                                                ?.vote === 'downvote'
                                                ? 'ri:thumb-down-fill'
                                                : 'ri:thumb-down-line'
                                        "
                                        :title="$t('action.downvote')"
                                        modifiers="action-quiet-small"
                                        @click.stop="
                                            $emit('downvote', message)
                                        " />
                                </transition>
                                <transition mode="out-in">
                                    <VvButton
                                        v-if="
                                            isActionButtonVisible(
                                                'feedback',
                                                index,
                                            )
                                        "
                                        :icon="
                                            getMessageFeedback(message.id)
                                                ?.comment
                                                ? 'ri:feedback-fill'
                                                : 'ri:feedback-line'
                                        "
                                        :title="$t('action.feedback')"
                                        modifiers="action-quiet-small"
                                        @click.stop="
                                            $emit('feedback', message)
                                        " />
                                </transition>
                            </VvButtonGroup>
                            <span
                                v-if="
                                    showMessageTokensCount &&
                                    message.metadata?.totalTokens
                                "
                                class="flex items-center gap-4 text-12 text-word-3">
                                <VvIcon name="ri:ai-generate-2-line" />
                                {{
                                    $n(message.metadata.totalTokens, 'integer')
                                }}
                                {{ $t('label.tokens') }}
                            </span>
                            <time
                                v-if="
                                    showMessageDateTime &&
                                    message.metadata?.createdAt
                                "
                                :datetime="
                                    new Date(
                                        message.metadata?.createdAt,
                                    ).toISOString()
                                "
                                class="flex items-center gap-4 text-12 text-word-3">
                                <VvIcon name="ri:time-line" />
                                {{
                                    $d(
                                        new Date(message.metadata?.createdAt),
                                        'date-time',
                                    )
                                }}
                            </time>
                            <div
                                v-if="
                                    showMessageDateTime &&
                                    message?.metadata?.completedAt &&
                                    message?.metadata?.createdAt
                                "
                                class="flex items-center gap-4 text-12 text-word-3">
                                <VvIcon name="ri:hourglass-line" />
                                <PkRelativeTime
                                    :date="message.metadata?.createdAt"
                                    :end-date="message.metadata?.completedAt" />
                            </div>
                        </div>
                    </transition>
                    <transition mode="out-in">
                        <PkChatbotFeedbackForm
                            v-if="message.id === feedbackMessageId"
                            ref="feedbackMessageEl"
                            :loading="feedbackLoading"
                            :submitted="feedbackSubmitted"
                            :error="feedbackError"
                            @submit="$emit('feedback-submit', $event)"
                            @close="$emit('feedback-close')" />
                    </transition>
                </div>
            </template>
            <PkChatbotError
                v-if="isError"
                :error
                @retry="$emit('auto-retry')"
                @reset="$emit('reset-chat')" />
            <div
                v-if="activeMessage?.role === 'user' || isError"
                :style="{ minHeight: `${height}px` }"></div>
        </div>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-messages {
        overflow-y: auto;
        flex: 1;
        min-width: 0;
        font-size: var(--spacing-14);

        scrollbar-width: thin;
        scrollbar-gutter: auto;
        scrollbar-color: var(--color-word-5) var(--color-surface-1);

        &__wrapper {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-16);
            min-width: 0;
        }

        &__divider {
            border-bottom: 1px solid var(--color-surface-3);
        }
    }

    .pk-chatbot-divider {
        position: relative;
        border-bottom: 1px solid var(--color-surface-3);

        &__label {
            position: absolute;
            padding-inline: var(--spacing-8);
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: var(--color-surface);
            color: var(--color-word-4);
            font-size: var(--text-12);
        }
    }

    .pk-chatbot-message {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
        min-width: 0;
        align-items: flex-start;

        &__text {
            color: var(--color-word-2);
            line-height: var(--leading-normal);
            border-width: var(--spacing-px);
            border-color: var(--color-surface-3);
            padding: var(--spacing-sm);
            border-radius: var(--rounded-xl) var(--rounded-xl) var(--rounded-xl)
                0;
        }

        &__loading-info {
            line-height: var(--leading-normal);
            border-width: var(--spacing-px);
            border-color: var(--color-surface-3);
            padding: var(--spacing-sm);
            border-radius: var(--rounded-xl) var(--rounded-xl) var(--rounded-xl)
                0;
            display: flex;
            gap: var(--spacing-8);
            font-size: var(--text-12);
            color: var(--color-word-3);
            align-items: center;
        }

        &__footer {
            width: 100%;
            display: flex;
            gap: var(--spacing-8);
            align-items: center;
        }

        &--user {
            align-items: flex-end;

            .pk-chatbot-message__text {
                background-color: var(
                    --chatbot-main-color,
                    var(--color-surface-1)
                );
                border-width: var(--spacing-px);
                border-color: var(--chatbot-main-color, var(--color-surface-4));
                color: var(--chatbot-contrast-color, var(--color-word-1));
                border-radius: var(--rounded-xl) var(--rounded-xl) 0
                    var(--rounded-xl);
            }
        }

        &--assistant {
            width: 100%;
            display: flex;
            min-width: 0;
            flex-direction: column;
            gap: var(--spacing-16);
            overflow: hidden;

            .pk-chatbot-message__text {
                width: 100%;
            }
        }

        &--system {
            align-items: flex-end;

            .pk-chatbot-message__text {
                display: flex;
                gap: var(--spacing-8);
                font-size: var(--text-12);
                padding: var(--spacing-8) var(--spacing-sm);
                align-items: center;
                border-width: var(--spacing-px);
                border-color: var(--chatbot-main-color, var(--color-surface-4));
                color: var(--chatbot-main-color, var(--color-word-1));
                border-radius: var(--rounded-xl) var(--rounded-xl) 0
                    var(--rounded-xl);
            }
        }
    }
</style>
